<template>
    <Teleport to="body">
        <Transition name="modal">
            <div v-if="isOpen" class="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
                <!-- Backdrop -->
                <div class="absolute inset-0 bg-black/70 backdrop-blur-sm" @click="$emit('close')" />

                <!-- Success State -->
                <div
                    v-if="showSuccess"
                    ref="successModalRef"
                    class="relative w-full max-w-md max-h-[100dvh] sm:max-h-[90vh] flex flex-col bg-zinc-950 rounded-t-3xl sm:rounded-2xl border-t border-white/10 sm:border border-white/10 shadow-2xl"
                    :style="successModalStyle"
                    @touchstart="handleTouchStart"
                    @touchmove="handleTouchMove"
                    @touchend="handleTouchEnd"
                >
                    <!-- Mobile Pull Handle -->
                    <div class="sm:hidden flex justify-center pt-2 pb-0.5 cursor-grab active:cursor-grabbing">
                        <div class="w-10 h-1 rounded-full bg-zinc-700" />
                    </div>

                    <!-- Close Button -->
                    <button
                        @click="closeModal"
                        class="absolute top-2.5 right-2.5 sm:top-3 sm:right-3 z-10 w-8 h-8 rounded-lg hover:bg-white/10 flex items-center justify-center transition-colors active:scale-95"
                        aria-label="Close"
                    >
                        <X class="w-4 h-4 text-white/70" />
                    </button>

                    <!-- Success Content -->
                    <div class="px-5 pt-6 pb-5 sm:px-6 sm:pt-8 sm:pb-6">
                        <div class="flex flex-col items-center mb-6">
                            <div class="w-20 h-20 rounded-full bg-emerald-500/15 border-2 border-emerald-500/30 flex items-center justify-center mb-4">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="-0.5 -0.5 16 16" fill="none" stroke-linecap="round" stroke-linejoin="round" class="w-10 h-10 stroke-emerald-400" stroke-width="1">
                                    <path d="M13.75 6.925V7.5a6.25 6.25 0 1 1 -3.70625 -5.7125"></path>
                                    <path d="M13.75 2.5 7.5 8.75625l-1.875 -1.875"></path>
                                </svg>
                            </div>

                            <h2 class="text-emerald-400 text-2xl font-bold mb-3">
                                You Gave Your First Star!
                            </h2>

                            <p class="text-white/70 text-center text-sm leading-relaxed mb-1">
                                You just sponsored your first audition — you're now part of the community deciding what gets filmed.
                            </p>

                            <p class="text-white/50 text-center text-xs leading-relaxed">
                                Keep giving stars to shape the content you want to see
                            </p>
                        </div>

                        <!-- Star Animation -->
                        <div class="flex justify-center gap-1 mb-6">
                            <img
                                :src="STAR_ICON"
                                alt=""
                                class="w-8 h-8 first-star-bounce"
                            />
                        </div>

                        <!-- Action Buttons -->
                        <div class="space-y-2.5">
                            <button
                                @click="$emit('openFullModal')"
                                class="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl text-sm transition-all active:scale-95 flex items-center justify-center gap-2"
                            >
                                <img :src="STAR_ICON" alt="" class="w-4 h-4" />
                                Give More Stars
                            </button>

                            <button
                                @click="closeModal"
                                class="w-full py-2 text-white/60 hover:text-white text-sm font-medium transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Main Modal -->
                <div
                    v-else
                    ref="mainModalRef"
                    class="relative w-full max-w-md max-h-[100dvh] sm:max-h-[90vh] flex flex-col bg-zinc-950 rounded-t-3xl sm:rounded-2xl border-t border-white/10 sm:border border-white/10 shadow-2xl"
                    :style="mainModalStyle"
                    @touchstart="handleTouchStart"
                    @touchmove="handleTouchMove"
                    @touchend="handleTouchEnd"
                >
                    <!-- Mobile Pull Handle -->
                    <div class="sm:hidden flex justify-center pt-2 pb-0.5 cursor-grab active:cursor-grabbing shrink-0">
                        <div class="w-10 h-1 rounded-full bg-zinc-700" />
                    </div>

                    <!-- Close Button -->
                    <button
                        @click="$emit('close')"
                        class="absolute top-2.5 right-2.5 sm:top-3 sm:right-3 z-10 w-8 h-8 rounded-lg hover:bg-white/10 flex items-center justify-center transition-colors active:scale-95"
                        aria-label="Close"
                    >
                        <X class="w-4 h-4 text-white/70" />
                    </button>

                    <!-- Content -->
                    <div class="px-5 pt-3 pb-4 sm:px-6 sm:pt-5 sm:pb-6 overflow-y-auto min-h-0">
                        <!-- Hero -->
                        <div class="flex flex-col items-center mb-4 sm:mb-5">
                            <div class="relative mb-3">
                                <div class="absolute inset-0 bg-blue-500/20 blur-xl rounded-full scale-150" />
                                <img :src="STAR_ICON" alt="" class="relative w-14 h-14 drop-shadow-[0_0_12px_rgba(245,158,11,0.5)]" />
                            </div>
                            <h2 class="text-xl font-bold text-white mb-1">
                                Give Your First Star
                            </h2>
                            <p class="text-white/60 text-sm text-center leading-snug">
                                Your star helps decide what gets filmed next
                            </p>
                        </div>

                        <!-- Audition Info -->
                        <div v-if="audition" class="bg-zinc-900/60 rounded-xl p-3.5 border border-white/10 mb-4 sm:mb-5">
                            <div class="flex items-center gap-2.5 mb-2.5">
                                <div class="w-11 h-11 rounded-full overflow-hidden border border-white/10 shrink-0">
                                    <img :src="audition.creator?.avatar" :alt="audition.creator?.name" class="w-full h-full object-cover" />
                                </div>
                                <div class="min-w-0 flex-1">
                                    <h3 class="text-white font-semibold text-xs truncate leading-tight">
                                        {{ audition.title }}
                                    </h3>
                                    <p class="text-white/60 text-sm mt-0.5">{{ audition.creator?.name }}</p>
                                </div>
                            </div>

                            <!-- Progress -->
                            <div>
                                <div class="flex items-center justify-between mb-1.5">
                                    <span class="text-white font-semibold text-xs">Sponsor Goal</span>
                                    <span class="text-white text-xs font-semibold">
                                        {{ audition.likes }} / {{ audition.star_goal }}
                                    </span>
                                </div>
                                <div class="relative h-1.5 bg-white/10 rounded-full overflow-hidden mb-1">
                                    <div
                                        class="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-600 to-blue-500 rounded-full transition-all duration-500"
                                        :style="{ width: `${Math.min(percentage, 100)}%` }"
                                    />
                                </div>
                                <div class="flex items-center justify-between">
                                    <span class="text-white/60 text-xs">{{ starsToGo }} to go</span>
                                    <span class="text-white/60 text-xs">{{ Math.round(percentage) }}%</span>
                                </div>
                            </div>
                        </div>

                        <!-- Give Star CTA (has balance) -->
                        <div v-if="userBalance > 0">
                            <!-- Amount selector -->
                            <div class="bg-zinc-900 rounded-xl border border-white/10 mb-3">
                                <!-- Balance -->
                                <div class="flex items-center justify-between px-4 pt-2.5 mb-3">
                                    <span class="text-white/70 text-xs font-semibold">Your Balance</span>
                                    <div class="flex items-center gap-1.5">
                                        <img :src="STAR_ICON" alt="" class="w-4 h-4" />
                                        <span class="text-white text-sm font-bold">{{ userBalance }}</span>
                                    </div>
                                </div>

                                <!-- Amount display -->
                                <div class="flex items-center justify-center gap-2 mb-2 -ml-2 px-4">
                                    <img :src="STAR_ICON" alt="" class="w-7 h-7" />
                                    <span class="text-white text-3xl font-bold">{{ selectedAmount }}</span>
                                </div>

                                <!-- Slider with +/- buttons -->
                                <div class="flex items-center gap-2 px-4">
                                    <button
                                        @click="decrementAmount"
                                        :disabled="selectedAmount <= 1"
                                        :class="[
                                            'w-8 h-8 rounded-lg flex items-center justify-center text-lg font-bold transition-all shrink-0 active:scale-90',
                                            selectedAmount <= 1
                                                ? 'bg-white/5 text-white/20 cursor-not-allowed'
                                                : 'bg-white/10 text-white hover:bg-white/20'
                                        ]"
                                    >&minus;</button>
                                    <div class="flex-1 px-1">
                                        <input
                                            type="range"
                                            min="1"
                                            :max="userBalance"
                                            step="1"
                                            v-model.number="selectedAmount"
                                            class="star-slider w-full"
                                            :style="{ '--slider-pct': sliderPct }"
                                        />
                                    </div>
                                    <button
                                        @click="incrementAmount"
                                        :disabled="selectedAmount >= userBalance"
                                        :class="[
                                            'w-8 h-8 rounded-lg flex items-center justify-center text-lg font-bold transition-all shrink-0 active:scale-90',
                                            selectedAmount >= userBalance
                                                ? 'bg-white/5 text-white/20 cursor-not-allowed'
                                                : 'bg-white/10 text-white hover:bg-white/20'
                                        ]"
                                    >+</button>
                                </div>

                                <!-- Slider labels -->
                                <div class="flex items-center justify-between mt-1.5 px-[52px]">
                                    <span class="text-white/40 text-[10px] font-medium">1</span>
                                    <span class="text-white/40 text-[10px] font-medium">{{ userBalance }}</span>
                                </div>

                                <!-- Divider -->
                                <div class="border-t border-white/[0.06] mx-4 mt-3 mb-2.5"></div>

                                <!-- Quick presets -->
                                <div class="grid grid-cols-5 gap-1.5 px-4 pb-3">
                                    <button
                                        v-for="amount in quickAmounts"
                                        :key="amount"
                                        @click="selectedAmount = amount === 'all' ? userBalance : Math.min(amount, userBalance)"
                                        :disabled="amount !== 'all' && amount > userBalance"
                                        :class="[
                                            'py-2 rounded-xl font-bold transition-colors duration-75 border',
                                            (amount === 'all' ? selectedAmount === userBalance : selectedAmount === amount)
                                                ? 'bg-blue-600 text-white border-blue-500 scale-95'
                                                : (amount !== 'all' && amount > userBalance)
                                                ? 'bg-zinc-800 text-white/20 border-white/5 cursor-not-allowed'
                                                : 'bg-zinc-800 text-white border-white/[0.06] hover:border-white/20 hover:bg-zinc-700 active:scale-95'
                                        ]"
                                    >
                                        <div class="flex flex-col items-center gap-0.5">
                                            <img :src="STAR_ICON" alt="" class="w-4 h-4" :class="(amount !== 'all' && amount > userBalance) ? 'opacity-20' : ''" />
                                            <span class="text-xs">{{ amount === 'all' ? 'All' : amount }}</span>
                                        </div>
                                    </button>
                                </div>
                            </div>

                            <button
                                @click="handleSubmit"
                                :disabled="loading"
                                :class="[
                                    'w-full py-3.5 font-bold rounded-xl text-sm transition-all flex items-center justify-center gap-2',
                                    !loading
                                        ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/25 active:scale-95'
                                        : 'bg-zinc-800 text-white/30 cursor-not-allowed'
                                ]"
                            >
                                <svg
                                    v-if="loading"
                                    class="animate-spin h-4 w-4"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <template v-if="!loading">
                                    <img :src="STAR_ICON" alt="" class="w-5 h-5" />
                                    Give {{ selectedAmount }} Star{{ selectedAmount !== 1 ? 's' : '' }}
                                </template>
                                <template v-else>Processing...</template>
                            </button>
                        </div>

                        <!-- No balance — Buy Stars CTA -->
                        <div v-else class="bg-amber-500/10 rounded-xl px-4 py-3 border border-amber-500/30">
                            <div class="flex items-center gap-2.5 mb-2">
                                <div class="w-6 h-6 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center flex-shrink-0">
                                    <Star class="w-3.5 h-3.5 text-black" fill="black" :stroke-width="0" />
                                </div>
                                <p class="text-amber-300 text-sm font-semibold">
                                    You need stars to sponsor
                                </p>
                            </div>
                            <p class="text-white/60 text-xs mb-2.5">
                                Buy a star pack to start sponsoring your favorite performers.
                            </p>
                            <button
                                @click="$emit('buyStars')"
                                class="w-full py-3 bg-amber-600 hover:bg-amber-700 active:scale-[0.98] text-white text-sm font-semibold rounded-xl transition-all shadow-lg shadow-amber-600/20"
                            >
                                Buy Stars to Get Started
                            </button>
                        </div>

                        <p v-if="errorMessage" class="text-red-400 text-sm text-center mt-4">{{ errorMessage }}</p>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

import { X, Star } from 'lucide-vue-next'
import { trackFathomEvent, getFathomPostPrefix } from "@/Helpers/analytics"

const STAR_ICON = 'https://sm.naughtycdn.com/images/landers/prize/NA-Star.png'

const props = defineProps({
    isOpen: {
        type: Boolean,
        required: true
    },
    audition: {
        type: Object,
        default: null
    },
    userBalance: {
        type: Number,
        required: true
    },
    loading: {
        type: Boolean,
        default: false
    },
    errorMessage: {
        type: String,
        default: ''
    },
    showSuccess: {
        type: Boolean,
        default: false
    },
    routePrefix: {
        type: String,
        default: ''
    }
})

const fathomPrefix = computed(() => getFathomPostPrefix(props.routePrefix))

const emit = defineEmits(['close', 'submit', 'resetSuccess', 'buyStars', 'openFullModal'])

const selectedAmount = ref(props.userBalance)
const quickAmounts = [5, 10, 50, 100, 'all']

const sliderPct = computed(() =>
    props.userBalance > 1
        ? (((selectedAmount.value - 1) / (props.userBalance - 1)) * 100) + '%'
        : '0%'
)

const decrementAmount = () => { if (selectedAmount.value > 1) selectedAmount.value-- }
const incrementAmount = () => { if (selectedAmount.value < props.userBalance) selectedAmount.value++ }

// Reset to full balance whenever the modal opens
watch(() => props.isOpen, (open) => { if (open) selectedAmount.value = props.userBalance })

const percentage = computed(() => {
    if (!props.audition) return 0
    return (props.audition.likes / props.audition.star_goal) * 100
})
const starsToGo = computed(() => {
    if (!props.audition) return 0
    return Math.max(0, props.audition.star_goal - props.audition.likes)
})

const handleSubmit = () => {
    if (props.userBalance > 0 && selectedAmount.value > 0) {
        trackFathomEvent(`${fathomPrefix.value}_Modal_Give_First_Star_Clicked`)
        emit('submit', selectedAmount.value, null, false, 'first_star')
    }
}

const closeModal = () => {
    emit('resetSuccess')
    emit('close')
}

// Touch/swipe handling (same pattern as GiveStarsModal)
const mainModalRef = ref(null)
const successModalRef = ref(null)
const touchStartY = ref(0)
const touchCurrentY = ref(0)
const isDragging = ref(false)

const mainModalStyle = computed(() => {
    if (!isDragging.value || props.showSuccess) return {}
    const deltaY = touchCurrentY.value - touchStartY.value
    if (deltaY < 0) return {}
    return { transform: `translateY(${deltaY}px)`, transition: 'none' }
})

const successModalStyle = computed(() => {
    if (!isDragging.value || !props.showSuccess) return {}
    const deltaY = touchCurrentY.value - touchStartY.value
    if (deltaY < 0) return {}
    return { transform: `translateY(${deltaY}px)`, transition: 'none' }
})

const handleTouchStart = (e) => {
    const modalRef = props.showSuccess ? successModalRef.value : mainModalRef.value
    if (!modalRef) return
    const touch = e.touches[0]
    const rect = modalRef.getBoundingClientRect()
    if (touch.clientY - rect.top > 60) return
    touchStartY.value = touch.clientY
    touchCurrentY.value = touch.clientY
    isDragging.value = true
}

const handleTouchMove = (e) => {
    if (!isDragging.value) return
    const touch = e.touches[0]
    touchCurrentY.value = touch.clientY
    const deltaY = touchCurrentY.value - touchStartY.value
    if (deltaY > 0) e.preventDefault()
}

const handleTouchEnd = () => {
    if (!isDragging.value) return
    const deltaY = touchCurrentY.value - touchStartY.value
    if (deltaY > 100) {
        if (props.showSuccess) {
            closeModal()
        } else {
            emit('close')
        }
    }
    isDragging.value = false
    touchStartY.value = 0
    touchCurrentY.value = 0
}
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
    transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
    opacity: 0;
}

.star-slider {
    -webkit-appearance: none;
    appearance: none;
    height: 6px;
    border-radius: 9999px;
    background: linear-gradient(to right,
        #2563eb 0%,
        #2563eb var(--slider-pct, 0%),
        rgba(255,255,255,0.1) var(--slider-pct, 0%),
        rgba(255,255,255,0.1) 100%
    );
    outline: none;
    cursor: pointer;
}
.star-slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #2563eb;
    border: 3px solid #1d4ed8;
    box-shadow: 0 0 8px rgba(37,99,235,0.4);
    cursor: pointer;
    transition: transform 0.15s ease;
}
.star-slider::-webkit-slider-thumb:hover { transform: scale(1.15); }
.star-slider::-moz-range-thumb {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #2563eb;
    border: 3px solid #1d4ed8;
    box-shadow: 0 0 8px rgba(37,99,235,0.4);
    cursor: pointer;
}
.star-slider::-moz-range-track {
    height: 6px;
    border-radius: 9999px;
    background: rgba(255,255,255,0.1);
}

@keyframes first-star-bounce {
    0%   { transform: scale(0.3) rotate(-30deg); opacity: 0; }
    50%  { transform: scale(1.3) rotate(10deg); opacity: 1; }
    70%  { transform: scale(0.9) rotate(-5deg); }
    100% { transform: scale(1) rotate(0deg); opacity: 1; }
}

.first-star-bounce {
    animation: first-star-bounce 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) both;
    animation-delay: 0.2s;
}
</style>
